// src/components/Admin/ItemManager/index.jsx
import { useEffect, useState } from 'react';
import {
  Box, Tabs, Tab, Button, Paper,
} from '@mui/material';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';

import api from '../../../api/axios';

import ItemTable from './ItemTable';
import ItemFormDialog from './ItemFormDialog';
import ItemUsageLog from './ItemUsageLog';
import ItemTransactionLog from './ItemTransactionLog';
import { buildWsUrl } from '../../../utils/websocket'; // ✅ WebSocket 주소 유틸

const ItemManager = () => {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [hasPendingAction, setHasPendingAction] = useState(false); // ✅ 추가

  useEffect(() => {
    const ws = new WebSocket(buildWsUrl('/ws/admin/items/'));

    ws.onopen = () => console.log('✅ Admin WebSocket connected');
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'item.action_request') {
          console.log('🛎️ 새 쿠폰 승인 요청:', data);
          setHasPendingAction(true);
        }
      } catch (e) {
        console.error('❌ WebSocket message error:', e);
      }
    };
    ws.onclose = () => console.log('❌ Admin WebSocket closed');

    return () => ws.close(); // cleanup on unmount
  }, []);

  const fetchItems = () => {
    api.get('/api/rewards/item-admin/')
      .then((res) => setItems(res.data.items || []))
      .catch(() => alert('아이템 목록 불러오기 실패'));
  };

  useEffect(() => { fetchItems(); }, []);

  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    if (newTab === 1) setHasPendingAction(false); // ✅ '승인/환불' 탭 클릭 시 깜빡임 제거
  };

  const handleAddItem = () => { setEditingItem(null); setFormOpen(true); };
  const handleEditItem = (item) => {
    setEditingItem({
      ...item,
      price: item.price ?? 0,
      stock: item.stock ?? 0,
      is_active: item.is_active ?? true,
      is_unlimited: item.is_unlimited ?? true,
      is_coupon: item.is_coupon ?? true,
      description: item.description ?? '',
    });
    setFormOpen(true);
  };
  const handleDeleteItem = (id) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    api.delete(`/api/rewards/item-admin/${id}/delete/`)
      .then(fetchItems)
      .catch(() => alert('삭제 실패'));
  };
  const handleToggleActive = (id, cur) => {
    api.patch(`/api/rewards/item-admin/${id}/update/`, { is_active: !cur })
      .then(fetchItems)
      .catch(() => alert('노출 상태 변경 실패'));
  };

  return (
    <Box sx={{
      p: 4,
      '@media (max-width:600px)': {
        transform: 'scale(0.9)',
        transformOrigin: 'top left',
        p: 2,
      },
    }}>
      <Paper elevation={2} sx={{
        mb: 3,
        borderRadius: 2,
        backgroundColor: '#f5f5f5',
        px: 2,
        '@media (max-width:600px)': { px: 1 },
      }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              '@media (max-width:600px)': { minHeight: 40, fontSize: '0.8rem' },
            },
          }}
        >
          <Tab icon={<Inventory2Icon />} iconPosition="start" label="아이템 목록" />
          <Tab
            icon={<AssignmentTurnedInIcon />}
            iconPosition="start"
            label="승인/환불"
            sx={hasPendingAction ? {
              color: 'error.main',
              animation: 'blinker 1.2s linear infinite',
            } : {}}
          />
          <Tab icon={<ShoppingCartCheckoutIcon />} iconPosition="start" label="구매 로그" />
        </Tabs>
      </Paper>

      {tab === 0 && (
        <>
          <Button variant="contained" sx={{
            mb: 2,
            '@media (max-width:600px)': { fontSize: '0.8rem', px: 1.6 },
          }} onClick={handleAddItem}>
            쿠폰/아이템 등록
          </Button>
          <ItemTable
            items={items}
            onEdit={handleEditItem}
            onDelete={handleDeleteItem}
            onToggleActive={handleToggleActive}
          />
        </>
      )}

      {tab === 1 && <ItemUsageLog />}
      {tab === 2 && <ItemTransactionLog />}

      <ItemFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        item={editingItem}
        onSaved={fetchItems}
      />
    </Box>
  );
};

export default ItemManager;